import { Plus, Search, Upload } from 'lucide-react'
import { useCallback } from 'react'

import Flex from '@/elements/Flex'
import FormInput from '@/elements/FormInput'
import Button from '@/elements/Button'
import UploadModal from '@/components/UploadModal'
import useBreakpoints from '@/hooks/useBreakpoints'
import { uploadOrganizationIndividualsCSV } from '@/api/organizationIndividual'
import toast from '@/utils/toast'
import type { UploadErrorsType } from '@/hooks/useUploadErrors'
import { INDIVIDUALS_CSV_TEMPLATE } from '@/constants'

interface OrganizationIndividualsFilterBarProps {
  filterByName: string
  setFilterByName: (name: string) => void
  refetchIndividuals: () => void
  handleOpenAddUserModal?: () => void
}

function OrganizationIndividualsFilterBar({
  filterByName,
  setFilterByName,
  refetchIndividuals,
  handleOpenAddUserModal
}: OrganizationIndividualsFilterBarProps) {
  const { isTablet, isMobile } = useBreakpoints()

  const handleUploadIndividualsCSV = useCallback(async (files: File[]) => {
    if (files.length === 0) return null

    const formData = new FormData()
    formData.append('csv', files[0])

    try {
      await uploadOrganizationIndividualsCSV(formData)
      toast.success('Individuals CSV uploaded successfully!')
      refetchIndividuals()
      return null
    } catch (error: any) {
      const responseError = error.errors as UploadErrorsType
      toast.error(error.message || 'Failed to upload Individuals CSV.')
      return responseError
    }
  }, [refetchIndividuals])

  return (
    <Flex inline wfull justify='space-between' align='end' gh={4} gv={3} wrap >
      <Flex inline={!isMobile} grow={!isMobile} align='end' gap={3} gv={2} wfull={isTablet} wrap={isMobile}>
        <FormInput
          name='search'
          placeholder='Search individuals...'
          Icon={Search}
          value={filterByName}
          onChangeDebounced={val => setFilterByName(val)}
          style={{ width: '100%' }}
        />
      </Flex>
      <Flex inline align='end' grow gap={3} gv={2} wfull={isTablet} wrap={isMobile}>
        <UploadModal
          title='Upload Individuals CSV'
          description='Select CSV file to upload'
          accept={{ 'text/csv': ['.csv'] }}
          onUpload={handleUploadIndividualsCSV}
          sampleFileUrl={INDIVIDUALS_CSV_TEMPLATE}
        >
          <Button fullWidth>
            <Upload size={18} style={{ minWidth: 18 }} />
            Upload CSV
          </Button>
        </UploadModal>
        <Button variant='primary' fullWidth onClick={handleOpenAddUserModal}>
          <Plus size={18} style={{ minWidth: 18 }} />
          Add Individual
        </Button>
      </Flex>
    </Flex>
  )
}

export default OrganizationIndividualsFilterBar
